import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { interval, Subject, throwError } from 'rxjs';
import { catchError, filter, map, retry, switchMap, takeUntil, tap, throttle } from 'rxjs/operators';
import { County, ICounty } from '../../../foundation/models/enums/county.enum';
import { IJurisdiction, Jurisdiction } from '../../../foundation/models/enums/jurisdiction.enum';
import { CustomerCategoryType } from '../../models/enums/customer-category-type.enum';
import { CustomerIdentificationType } from '../../models/enums/customer-identification-type.enum';
import { IndividualSearchCriteria } from '../../models/individual-search-criteria.model';
import { CustomerSearchService } from '../../shared/customer-search.service';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { Panel } from 'primeng/panel';

const DEFAULT_ID_TYPE = 7;
const NATIVE_JURISDICTION = 4;

@Component({
    selector: 'individual-search',
    templateUrl: './individual-search.html',
})
export class IndividualSearchComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() data: any = {};
    @Input() searchInfo: any;
    @Input() defaultCustomerIdentificationTypeId: number = DEFAULT_ID_TYPE;
    @Input() includeDeceased = true;
    @Input() showSearchCriteria = true;
    @Input() showSearchCriteriaCheckboxes = true;
    @Input() showResults = false;
    @Input() maskSSN = false;
    @Output() onAddCustomerClicked = new EventEmitter<IndividualSearchCriteria>();
    @Output() onCustomerSearching = new EventEmitter<boolean>();
    @Output() onFormReset = new EventEmitter<any>();
    @Output() onResultsReceived = new EventEmitter<any>();

    @ViewChild('toggleablePanel') toggleablePanel: Panel;

    public counties: ICounty[];
    public customerIdentificationTypes: any[];
    public identificationTypes: CustomerIdentificationType;
    // public searchCriteria: IndividualSearchCriteria;
    public searchForm: UntypedFormGroup;
    public showButtonLoading = false;
    public states: IJurisdiction[];
    public validationMessage = '';
    private searchIndividual$ = new Subject<void>();
    private previousCustomerIdentificationTypeId: number;

    constructor(
        public fb: UntypedFormBuilder,
        public customerSearchService: CustomerSearchService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.setupSearchIndividualListener();
        this.setupEnums();
        this.initializeSearchForm();
        this.displayAnyResults();
        this.displayFirstValidationMessage();
    }

    public addNewCustomer(): void {
        let searchCriteria = this.prepareCriteriaForSearch();
        this.onAddCustomerClicked.emit(searchCriteria);
    }

    public customerIdentificationType(): string {
        return this.getValues(this.searchForm.get('customerIdentificationTypeId').value, this.identificationTypes);
    }

    public reset(): void {
        this.initializeSearchForm();
        this.showResults = false;
        this.showSearchCriteria = true;
        this.validationMessage = '';
        this.onFormReset.emit(null);
    }

    public searchIndividual(): void {
        this.searchIndividual$.next();
    }

    public setupSearchIndividualListener(): void {
        this.searchIndividual$
            .pipe(
                takeUntil(this.componentDestroyed),
                throttle(() => interval(500)),
                tap(() => {
                    this.onCustomerSearching.emit(true);
                    this.showResults = false;
                }),
                filter(() => this.searchForm.valid),
                tap(() => this.prepareViewPriorToSearch()),
                map(() => this.prepareCriteriaForSearch()),
                switchMap(preparedSearchCriteria => this.customerSearchService.searchIndividuals(preparedSearchCriteria)),
                tap(() => this.setCriteriaExactSearch()),
                tap(response => this.prepareViewFollowingSearch(response)),
                catchError(error => {
                    this.setCriteriaExactSearch();
                    this.prepareViewFollowingError(error);
                    return throwError(error);
                }),
                retry()
            )
            .subscribe(
                response => {
                    this.data = response;
                    this.onResultsReceived.emit(this.data);
                }
            );
    }

    public customerIdentificationTypeChange(customerIdentificationTypeId: number): void {
        if (this.previousCustomerIdentificationTypeId === this.identificationTypes.SSN.id && customerIdentificationTypeId !== this.identificationTypes.SSN.id && this.maskSSN) {
            this.searchForm.controls['customerIdentificationValue'].setValue('');
        }

        this.previousCustomerIdentificationTypeId = customerIdentificationTypeId;
    }

    private countyWrongStateValidator(nativeJurisdiction: number): (FormGroup) => { [key: string]: any } {
        return (group: UntypedFormGroup): { [key: string]: any } => {
            if ((group.controls['countyId'].value)
                && ((group.controls['jurisdictionId'].value || 0) != nativeJurisdiction)) {
                return { 'countyWrongState': true };
            }
        };
    }

    private displayAnyResults(): void {
        if (this.data && this.data.results) {
            this.showResults = true;
            this.showSearchCriteria = false;
        }
    }

    private displayFirstValidationMessage(): void {
        if (this.data && this.data.results) {
            if ((this.data.results.length > 0) &&
                this.data.results.every(r => r.validationResults && !!r.validationResults.length)
            ) {
                this.validationMessage = this.data.results[0].validationResults[0].errorMessage;
            }
        }
    }

    private getValues(id: number, types: any): string {
        if ((id != undefined) &&
            (id != null) &&
            (id != -1)
        ) {
            return types.FromId(id).displayValue;
        } else {
            return '';
        }
    }

    private idTypeValidator(group: UntypedFormGroup): { [key: string]: boolean } | null {
        let customerIdentificationValue = group.get('customerIdentificationValue').value;
        let customerIdentificationTypeId = group.get('customerIdentificationTypeId').value;

        if ((customerIdentificationValue != null) &&
            (customerIdentificationValue.length > 0) &&
            ((customerIdentificationTypeId == null) ||
             (customerIdentificationTypeId == 0))
        ) {
            return { 'idTypeRequired': true };
        }
    }

    private initializeSearchForm(): void {
        this.searchForm = this.fb.group({
            exactSearch: [this.searchInfo && this.searchInfo.exactSearch ? this.searchInfo.exactSearch : true],
            customerIdentificationTypeId: [this.searchInfo && this.searchInfo.customerIdentificationTypeId ? this.searchInfo.customerIdentificationTypeId : this.defaultCustomerIdentificationTypeId],
            customerIdentificationValue: [this.searchInfo && this.searchInfo.customerIdentificationValue ? this.searchInfo.customerIdentificationValue : '', Validators.maxLength(50)],
            firstName: [this.searchInfo && this.searchInfo.firstName ? this.searchInfo.firstName : '', Validators.maxLength(50)],
            middleName: [this.searchInfo && this.searchInfo.middleName ? this.searchInfo.middleName : '', Validators.maxLength(50)],
            lastName: [this.searchInfo && this.searchInfo.lastName ? this.searchInfo.lastName : '', Validators.maxLength(50)],
            dateOfBirth: [this.searchInfo && this.searchInfo.dateOfBirth ? this.searchInfo.dateOfBirth : ''],
            addressLine1: [this.searchInfo && this.searchInfo.addressLine1 ? this.searchInfo.addressLine1 : '', Validators.maxLength(40)],
            addressLine2: [this.searchInfo && this.searchInfo.addressLine2 ? this.searchInfo.addressLine2 : '', Validators.maxLength(40)],
            postalZipCode: [this.searchInfo && this.searchInfo.postalZipCode ? this.searchInfo.postalZipCode : '', Validators.maxLength(9)],
            city: [this.searchInfo && this.searchInfo.city ? this.searchInfo.city : '', Validators.maxLength(20)],
            jurisdictionId: [this.searchInfo && this.searchInfo.jurisdictionId ? this.searchInfo.jurisdictionId : null],
            countyId: [this.searchInfo && this.searchInfo.countyId ? this.searchInfo.countyId : null],
            phoneNumber: [this.searchInfo && this.searchInfo.phoneNumber ? this.searchInfo.phoneNumber : '', Validators.maxLength(100)],
            emailAddress: [this.searchInfo && this.searchInfo.emailAddress ? this.searchInfo.emailAddress : '', Validators.maxLength(100)]
        }, {
            validator: Validators.compose([
                this.lastNameOrIdNumberRequiredValidator,
                this.idTypeValidator,
                this.stateRequiredValidator,
                this.countyWrongStateValidator(NATIVE_JURISDICTION)
            ])
        });

        if (this.searchInfo) {
            // if form was populated with searchInfo, enable "Find" button (if it is valid) by marking form as dirty and running validators
            this.searchForm.updateValueAndValidity();
            this.searchForm.markAsDirty();
        }

        // clear out SSN if it is masked and user is change to a different customer identification type
        if (this.maskSSN && this.searchForm.value.customerIdentificationValue && this.searchForm.value.customerIdentificationTypeId === this.identificationTypes.SSN.id
            && this.searchForm.value.customerIdentificationValue.length && this.searchForm.value.customerIdentificationValue.length < 9) {
            this.searchForm.controls['customerIdentificationValue'].setValue('');
        }

        this.previousCustomerIdentificationTypeId = this.searchForm.value.customerIdentificationTypeId;
    }

    private lastNameOrIdNumberRequiredValidator(group: UntypedFormGroup): { [key: string]: boolean } | null {
        let lastName = group.get('lastName').value;
        let customerIdentificationValue = group.get('customerIdentificationValue').value;

        if (((lastName == null) || (lastName.length == 0)) &&
            ((customerIdentificationValue == null) || (customerIdentificationValue.length == 0))
        ) {
            return { 'lastNameOrIdNumberRequired': true };
        }
    }

    private prepareCriteriaForSearch(): IndividualSearchCriteria {
        let searchCriteria = new IndividualSearchCriteria();
        searchCriteria.exactSearch = true == this.searchForm.get('exactSearch').value;
        searchCriteria.customerIdentificationValue = this.searchForm.get('customerIdentificationValue').value;
        searchCriteria.firstName = this.searchForm.get('firstName').value;
        searchCriteria.middleName = this.searchForm.get('middleName').value;
        searchCriteria.lastName = this.searchForm.get('lastName').value;
        searchCriteria.dateOfBirth = this.searchForm.get('dateOfBirth').value;
        searchCriteria.addressLine1 = this.searchForm.get('addressLine1').value;
        searchCriteria.addressLine2 = this.searchForm.get('addressLine2').value;
        searchCriteria.postalCode = this.searchForm.get('postalZipCode').value;
        searchCriteria.city = this.searchForm.get('city').value;
        searchCriteria.jurisdictionId = this.searchForm.get('jurisdictionId').value;
        searchCriteria.countyId = this.searchForm.get('countyId').value;
        searchCriteria.phoneNumber = this.searchForm.get('phoneNumber').value;
        searchCriteria.emailAddress = this.searchForm.get('emailAddress').value;
        searchCriteria.includeDeceased = this.includeDeceased;

        if ((null != searchCriteria.customerIdentificationValue) &&
            (0 != searchCriteria.customerIdentificationValue.length)
        ) {
            searchCriteria.customerIdentificationTypeId = this.searchForm.get('customerIdentificationTypeId').value;
            searchCriteria.exactSearch = true;
        }

        if (searchCriteria.dateOfBirth instanceof Date) {
            // set time to noon to account for timezone correction issues when Arizona observed DST in 1918, 1942-45, and 1967 (see Bug 143317)
            searchCriteria.dateOfBirth.setHours(12);
        }

        return searchCriteria;
    }

    private prepareViewFollowingError(error: any): void {
        this.validationMessage = error.response && error.response.validationResults ? error.response.validationResults[0].errorMessage : '';
        this.showButtonLoading = false;
    }

    private prepareViewFollowingSearch(response: any): void {
        this.showButtonLoading = false;
        this.showResults = true;
        let resultTotalCount = response.results ? response.results.length : 0;
        this.showSearchCriteria = (resultTotalCount === 0) || (resultTotalCount > 250);
    }

    private prepareViewPriorToSearch(): void {
        this.showButtonLoading = true;
        this.showResults = false;
        this.data = [];
        this.validationMessage = '';
    }

    private setCriteriaExactSearch(): void {
        if (null != this.searchForm.get('customerIdentificationValue').value) {
            this.searchForm.get('exactSearch').setValue(false);
        }
    }

    private setupEnums(): void {
        this.identificationTypes = new CustomerIdentificationType();
        this.customerIdentificationTypes = this.identificationTypes.GetActive().filter(it => it.customerCategoryType == (new CustomerCategoryType).Individual.id || it.customerCategoryType == (new CustomerCategoryType).Both.id);
        this.states = new Jurisdiction().GetActive();
        this.counties = new County().GetActive();
    }

    private stateRequiredValidator(group: any): { [key: string]: boolean } | null {
        if ((group.controls.city.value != null) &&
            (group.controls.city.value.length > 0) &&
            ((group.controls.jurisdictionId.value == null) ||
             (group.controls.jurisdictionId.value == 0))
        ) {
            return { 'stateRequired': true };
        }
    }

    ngAfterViewInit(): void {
        this.toggleablePanel.collapseIcon = 'pi pi-plus';
        this.toggleablePanel.expandIcon = 'pi pi-minus';
    }
}
